import {gsap} from "gsap";
import {useGSAP} from '@gsap/react';
import {ScrollTrigger} from "gsap/ScrollTrigger";
import {useRef} from "react";
import {useAppState} from "@/store";
import DOMPurify from "dompurify";
import {formatPrice, parseMergeTags} from "@/helpers";

gsap.registerPlugin(ScrollTrigger);

export default function BillComparison() {

    const container = useRef(null)
    const {state} = useAppState()

    const content = window.ein?.content?.step_5 || {}
    const {moving_bill} = content

    useGSAP(() => {
        gsap.from('.ein-bill-line-item, .ein-bill-message, .ein-bill-total', {
            scrollTrigger: {
                trigger: container.current,
                start: 'center bottom 10%',
            },
            opacity: 0,
            duration: 0.25,
            stagger: 0.30
        });
    },{ scope: container });

    return (
        <>
            {moving_bill.heading && (
                <div className={"heading-h2 text-center mb-[16px]"}>
                    {parseMergeTags(moving_bill.heading, state)}
                </div>
            )}
            {moving_bill.text && (
                <div
                    className={"text-big text-center mb-[40px] [&>:last-child]:mb-0"}
                    dangerouslySetInnerHTML={{'__html': DOMPurify.sanitize(parseMergeTags(moving_bill.text, state))}}
                />
            )}

            <div className={"lg:flex lg:gap-[32px]"} ref={container}>
                <div className={"lg:w-1/2"}>
                    <Bill
                        color={"red"}
                        name={"TYPICAL MOVING COMPANY"}
                        rate={"BASE HOURLY RATE ????🤷"}
                        total={"Your Total $XXX+???"}
                    >
                        <div className={'ein-bill-line-items'}>
                            <BillLineItem label={"Credit Card Processing"} amount={"+3-4%"}/>
                            <BillLineItem label={"Fuel Surcharge"} amount={"+15-20%"}/>
                            <BillLineItem label={"Shrink Wrap & Tape"} amount={"+$75-150"}/>
                            <BillLineItem label={"TV Box Rental/Purchase"} amount={"+$25–40"}/>
                            <BillLineItem label={"Stairs/Long Carry Fee"} amount={"+$50-100"}/>
                            <BillLineItem label={"Assembly/Disassembly"} amount={"+$75+"}/>
                            <BillLineItem label={"Additional Insurance"} amount={"+$100+"}/>
                        </div>
                    </Bill>
                </div>
                <div className={"lg:w-1/2 mt-[24px] lg:mt-0"}>
                    <Bill
                        color={"blue"}
                        name={"🧠 EINSTEIN MOVING COMPANY"}
                        rate={`${state.branch.tripFees > 0 ? 'BASE HOURLY RATE' : 'ALL-INCLUSIVE HOURLY RATE'} ${formatPrice((state.availability?.adjustedRate || state.estimate?.rate) * state.estimate?.movers) || "XXX"}`}
                        total={`Your Total ${formatPrice(state.chosen_rate) || formatPrice(state.estimate?.totalCost) || "XXX"}`}
                    >
                        <div>
                            <BillLineItem label={"Everything included"} amount={"$0"}  />
                            {typeof state.branch?.tripFees !== 'undefined' && (
                                <BillLineItem label={"Trip Fee"} amount={`\$${state.branch.tripFees}`}/>
                            )}
                        </div>
                        <div className={"ein-bill-message leading-[22px] text-center mt-[10px]"}>
                            <strong>No Surprises, No Hidden Fees, No Nonsense</strong><br/>
                            Our price includes supplies, fuel, stairs, assembly, insurance, credit card processing, and more!
                        </div>
                    </Bill>
                </div>
            </div>
        </>
    )
}

function BillLineItem({label, amount}) {
    return (
        <div className={"flex ein-bill-line-item mb-[8px] last:mb-0"}>
            {label && (
                <div className={"truncate"}>
                    {label}
                </div>
            )}
            <div className={"whitespace-nowrap overflow-hidden flex-1"}>
                ..................................................
            </div>
            {amount && (
                <div className={"ein-bill-line-item-amount whitespace-nowrap"}>
                    {amount}
                </div>
            )}
        </div>
    )
}

function Bill({color, name, rate, total, children}) {
    return (
        <div className={"ein-bill " + (color ===
        'red' ? 'ein-bill-gradient-left' : 'ein-bill-gradient-right' )}>
            {name && (
                <div className={"text-[27px] text-center mb-[24px] font-bold uppercase leading-[normal]"}>
                    {name}
                </div>
            )}
            {rate && (
                <div className={"text-[22px] leading-[normal] py-[10px] border-y border-[#ccc] text-center mb-[24px] uppercase"}>
                    {rate}
                </div>
            )}
            {children}
            {total && (
                <div className={"ein-bill-total font-bold text-[22px] text-center mt-[24px] pt-[24px] border-t border-[#ccc] leading-[normal] " + (color ===
                'red' ? 'text-red' : 'text-brand-blue' )}>
                    {total}
                </div>
            )}
        </div>
    )
}
